import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import TimesheetSummary from './components/TimesheetSummary';
import TimesheetFilters from './components/TimesheetFilters';
import TimesheetEntryCard from './components/TimesheetEntryCard';
import TimesheetCalendarView from './components/TimesheetCalendarView';
import TimeTrackerForm from './components/TimeTrackerForm';
import { CheckInSystem } from './components/checkin';
import PayslipGenerator from './components/PayslipGenerator';
import timesheetCacheService from '../../services/timesheetCacheService';
import { exportTimesheetToExcel, validateExportData } from '../../services/exportService';
import TimesheetEntrySkeleton from '../../components/timesheet/TimesheetEntrySkeleton';
import TimesheetCalendarSkeleton from '../../components/timesheet/TimesheetCalendarSkeleton';

const SUMMARY_DEFAULTS = {
  totalHours: 0,
  approvedHours: 0,
  pendingHours: 0,
  rejectedHours: 0,
  totalAmount: 0,
  approvedAmount: 0
};

const VATimesheetPage = () => {
  const { userProfile } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    // Default to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    return {
      start: thirtyDaysAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(SUMMARY_DEFAULTS);
  const [trackingMode, setTrackingMode] = useState('checkin'); // 'checkin' or 'manual'
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [isExporting, setIsExporting] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  
  // Refs for cleanup and preventing memory leaks
  const subscriptionRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Handle window resize for responsive view
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      
      // Force list view on mobile
      if (!desktop && viewMode === 'calendar') {
        setViewMode('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  // Fetch VA's active contracts with Redis caching
  const fetchContracts = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      // Check cache first
      const cachedContracts = await timesheetCacheService.getContracts(userProfile.id);
      if (cachedContracts) {
        console.log('[VATimesheet] Using cached contracts');
        if (isMountedRef.current) {
          setContracts(cachedContracts);
        }
        return;
      }

      console.log('[VATimesheet] Cache miss, fetching contracts from database');
      const { data, error: contractsError } = await supabase
        .from('contracts')
        .select(`
          *,
          employer:user_profiles!employer_id (
            id,
            full_name,
            company_name,
            profile_picture
          ),
          projects (
            id,
            name
          ),
          jobs (
            id,
            title
          )
        `)
        .eq('va_id', userProfile.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (contractsError) throw contractsError;
      
      // Cache the result
      if (data) {
        await timesheetCacheService.setContracts(userProfile.id, data);
        console.log('[VATimesheet] Cached contracts data');
      }
      
      // Only update if component is still mounted
      if (isMountedRef.current) {
        setContracts(data || []);
      }
    } catch (err) {
      console.error('Error fetching contracts:', err);
      if (isMountedRef.current) {
        setError(`Failed to load contracts: ${err.message}`);
      }
      throw err;
    }
  }, [userProfile?.id]);

  // Calculate summary from entries - memoized for performance
  const calculateSummary = useCallback((entries, contractsData) => {
    if (!entries || entries.length === 0) {
      return SUMMARY_DEFAULTS;
    }

    const totalHours = entries.reduce((sum, entry) => sum + (parseFloat(entry.hours_logged) || 0), 0);
    const approvedHours = entries
      .filter(e => e.status === 'approved')
      .reduce((sum, entry) => sum + (parseFloat(entry.hours_logged) || 0), 0);
    const pendingHours = entries
      .filter(e => e.status === 'pending')
      .reduce((sum, entry) => sum + (parseFloat(entry.hours_logged) || 0), 0);
    const rejectedHours = entries
      .filter(e => e.status === 'rejected')
      .reduce((sum, entry) => sum + (parseFloat(entry.hours_logged) || 0), 0);

    // Calculate earnings - only iterate once
    let totalAmount = 0;
    let approvedAmount = 0;

    entries.forEach(entry => {
      const contract = contractsData.find(c => c.id === entry.contract_id);
      const hourlyRate = parseFloat(contract?.hourly_rate || 0);
      const hours = parseFloat(entry.hours_logged || 0);
      const amount = hours * hourlyRate;

      totalAmount += amount;
      if (entry.status === 'approved') {
        approvedAmount += amount;
      }
    });

    return {
      totalHours,
      approvedHours,
      pendingHours,
      rejectedHours,
      totalAmount,
      approvedAmount
    };
  }, []);

  // Fetch timesheet entries with filters - with Redis caching and on-demand warming
  const fetchTimesheetEntries = useCallback(async () => {
    if (!userProfile?.id || contracts.length === 0) return;

    try {
      // Build filter object for cache key
      const filters = {
        dateRange,
        statusFilter,
        contractFilter
      };

      // Try cache warming with on-demand data fetcher (Option C)
      const fetchFromDatabase = async () => {
        console.log('[VATimesheet] Fetching timesheet entries from database');

        // Use dateRange directly
        const startTime = dateRange?.start 
          ? new Date(dateRange.start + 'T00:00:00').toISOString()
          : new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
        
        const endTime = dateRange?.end 
          ? new Date(dateRange.end + 'T23:59:59').toISOString()
          : new Date().toISOString();
        
        let query = supabase
          .from('timesheet_entries')
          .select(`
            *,
            project:projects (
              id,
              name
            ),
            employer:user_profiles!employer_id (
              id,
              full_name,
              company_name
            )
          `)
          .eq('va_id', userProfile.id)
          .gte('start_time', startTime)
          .lte('start_time', endTime)
          .order('start_time', { ascending: false });

        // Apply status filter
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter);
        }

        // Apply contract filter
        if (contractFilter !== 'all') {
          query = query.eq('contract_id', contractFilter);
        }

        const { data, error: entriesError } = await query;

        if (entriesError) throw entriesError;

        return data || [];
      };

      // Warm cache (checks cache first, fetches if missing)
      const cachedOrFreshData = await timesheetCacheService.warmCache(
        userProfile.id,
        filters,
        fetchFromDatabase
      );

      if (isMountedRef.current && cachedOrFreshData) {
        setTimesheetEntries(cachedOrFreshData);
        
        // Calculate and update summary
        const newSummary = calculateSummary(cachedOrFreshData, contracts);
        setSummary(newSummary);
        
        // Also cache the summary
        await timesheetCacheService.setSummary(userProfile.id, filters, newSummary);
      }
    } catch (err) {
      console.error('Error fetching timesheet entries:', err);
      if (isMountedRef.current) {
        setError(`Failed to load timesheet: ${err.message}`);
      }
      throw err;
    }
  }, [userProfile?.id, contracts, dateRange, statusFilter, contractFilter, calculateSummary]);

  // Main data fetching effect - with proper cleanup
  const fetchAllData = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      await fetchContracts();
    } catch (err) {
      console.error('Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to load timesheet data');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [userProfile?.id, fetchContracts]);

  // Fetch contracts on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch timesheet entries when contracts, date range, status filter, or contract filter changes
  useEffect(() => {
    if (contracts.length > 0) {
      fetchTimesheetEntries();
    }
  }, [contracts, fetchTimesheetEntries]);

  // Real-time subscription for timesheet updates with cache invalidation
  useEffect(() => {
    if (!userProfile?.id) return;

    // Create subscription
    const channel = supabase
      .channel(`va_timesheet_${userProfile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'timesheet_entries',
          filter: `va_id=eq.${userProfile.id}`
        },
        async () => {
          if (isMountedRef.current) {
            console.log('[VATimesheet] Real-time update received, invalidating cache');
            
            // Invalidate all timesheet caches for this user
            await timesheetCacheService.invalidateAllTimesheetData(userProfile.id);
            
            // Refetch data (will be fresh from DB)
            fetchTimesheetEntries();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Timesheet subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('Timesheet subscription error:', status);
        }
        // 'CLOSED' status is expected when component unmounts, no need to log
      });

    subscriptionRef.current = channel;

    // Cleanup function
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [userProfile?.id, fetchTimesheetEntries]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleRefresh = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleExportToExcel = useCallback(async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Validate data
      const validation = validateExportData(timesheetEntries, userProfile);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Export to Excel
      await exportTimesheetToExcel({
        entries: timesheetEntries,
        summary,
        contracts,
        userProfile,
        dateRange,
        statusFilter,
        contractFilter
      });

      console.log('[VATimesheet] Successfully exported to Excel');
    } catch (err) {
      console.error('[VATimesheet] Export failed:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [timesheetEntries, summary, contracts, userProfile, dateRange, statusFilter, contractFilter]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading your timesheet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-error-50 border border-error-200 rounded-lg p-6">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-error mb-2">Error Loading Timesheet</h2>
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="outline" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <VAHeaderNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">My Contracts</h1>
              <p className="text-text-secondary mt-2">
                Track work hours and manage timesheet entries for your active contracts
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Check if VA has active contracts */}
        {contracts.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm border p-8">
              <Icon name="Briefcase" size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">
                No Active Contracts
              </h3>
              <p className="text-text-secondary mb-6">
                You need an active contract to track your time. Apply to jobs and get hired to start tracking your hours.
              </p>
              <Button
                variant="primary"
                iconName="Search"
                onClick={() => window.location.href = '/job-search'}
              >
                Find Jobs
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Main Layout with Sidebar */}
            <div className="flex gap-6 lg:gap-8">
              {/* Left Sidebar */}
              <aside className="hidden lg:block w-80 flex-shrink-0">
                <div className="sticky top-32 space-y-6">

                  {/* Filters */}
                  <div>
                    <TimesheetFilters
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                      statusFilter={statusFilter}
                      onStatusFilterChange={setStatusFilter}
                      contractFilter={contractFilter}
                      onContractFilterChange={setContractFilter}
                      contracts={contracts}
                      onExportClick={handleExportToExcel}
                      isExporting={isExporting}
                      canExport={timesheetEntries.length > 0}
                    />
                  </div>

                  {/* Payslip Generator */}
                <div className="mb-6">
                  <PayslipGenerator userProfile={userProfile} contracts={contracts} />
                </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Mobile Sidebar Toggle (visible on small screens) */}
                <div className="lg:hidden mb-8 space-y-5">
                  <div className="bg-white rounded-lg border p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Tracking</h3>
                    <div className="flex flex-col bg-gray-100 rounded-lg p-1 gap-1">
                      <button
                        onClick={() => setTrackingMode('checkin')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all text-left ${
                          trackingMode === 'checkin'
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon name="Camera" size={16} className="inline-block mr-2" />
                        Check-In System
                      </button>
                      <button
                        onClick={() => setTrackingMode('manual')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all text-left ${
                          trackingMode === 'manual'
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon name="Edit3" size={16} className="inline-block mr-2" />
                        Manual Entry
                      </button>
                    </div>
                    
                    <div className="mt-4">
                      {trackingMode === 'checkin' ? (
                        <CheckInSystem />
                      ) : (
                        <TimeTrackerForm 
                          contracts={contracts}
                          onEntryCreated={fetchTimesheetEntries}
                        />
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border shadow-sm">
                    <TimesheetFilters
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                      statusFilter={statusFilter}
                      onStatusFilterChange={setStatusFilter}
                      contractFilter={contractFilter}
                      onContractFilterChange={setContractFilter}
                      contracts={contracts}
                      onExportClick={handleExportToExcel}
                      isExporting={isExporting}
                      canExport={timesheetEntries.length > 0}
                    />
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="mb-8">
                  <TimesheetSummary summary={summary} dateRange={dateRange} />
                </div>

                {/* Tracking Mode Toggle - Desktop Only */}
                <div className="hidden lg:block bg-white rounded-lg border p-4 mb-8 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Tracking</h3>
                    <div className="flex flex-row justify-center bg-gray-100 rounded-lg p-1 gap-1">
                      <button
                        onClick={() => setTrackingMode('checkin')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all text-left ${
                          trackingMode === 'checkin'
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon name="Camera" size={16} className="inline-block mr-2" />
                        Check-In System
                      </button>
                      <button
                        onClick={() => setTrackingMode('manual')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all text-left ${
                          trackingMode === 'manual'
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon name="Edit3" size={16} className="inline-block mr-2" />
                        Manual Entry
                      </button>
                    </div>
                    
                    <div className="mt-4">
                      {trackingMode === 'checkin' ? (
                        <CheckInSystem />
                      ) : (
                        <TimeTrackerForm 
                          contracts={contracts}
                          onEntryCreated={fetchTimesheetEntries}
                        />
                      )}
                    </div>
                </div>

                {/* Timesheet Entries List */}
                <div className="bg-white rounded-lg border p-6 shadow-sm mt-8 lg:mt-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Timesheet Entries
                  {timesheetEntries.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-text-secondary">
                      ({timesheetEntries.length} {timesheetEntries.length === 1 ? 'entry' : 'entries'})
                    </span>
                  )}
                </h3>
                
                {/* View Toggle - Desktop Only */}
                {timesheetEntries.length > 0 && isDesktop && (
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                        viewMode === 'list'
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="List View"
                    >
                      <Icon name="List" size={16} className="inline-block mr-1" />
                      List
                    </button>
                    <button
                      onClick={() => setViewMode('calendar')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                        viewMode === 'calendar'
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Calendar View"
                    >
                      <Icon name="Calendar" size={16} className="inline-block mr-1" />
                      Calendar
                    </button>
                  </div>
                )}
              </div>
              
              {timesheetEntries.length > 0 || isLoading ? (
                <>
                  {/* List View - Always shown on mobile, toggleable on desktop */}
                  {(viewMode === 'list' || !isDesktop) && (
                    <div className="space-y-3">
                      {isLoading ? (
                        // Show skeleton loaders
                        [...Array(5)].map((_, index) => (
                          <TimesheetEntrySkeleton 
                            key={`skeleton-${index}`}
                            delay={index * 50}
                            viewMode="list"
                          />
                        ))
                      ) : (
                        // Show actual entries
                        timesheetEntries.map(entry => (
                          <TimesheetEntryCard
                            key={entry.id}
                            entry={entry}
                            contracts={contracts}
                            onUpdate={fetchTimesheetEntries}
                          />
                        ))
                      )}
                    </div>
                  )}
                  
                  {/* Calendar View - Desktop Only */}
                  {viewMode === 'calendar' && isDesktop && (
                    isLoading ? (
                      <TimesheetCalendarSkeleton />
                    ) : (
                      <TimesheetCalendarView
                        entries={timesheetEntries}
                        contracts={contracts}
                        dateRange={dateRange}
                        onEntryClick={(entry) => {
                          // Scroll to entry in list or show details
                          console.log('Calendar entry clicked:', entry);
                        }}
                      />
                    )
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Icon name="Clock" size={48} className="text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Time Entries</h4>
                  <p className="text-gray-500">
                    {statusFilter !== 'all' 
                      ? `No ${statusFilter} entries found for this period.`
                      : 'Start tracking your time to create timesheet entries.'
                    }
                  </p>
                </div>
              )}
            </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VATimesheetPage;
